import { TrackingInterval } from '@/features/projects/create-subproject/types/tracking-interval';
import {
  ProjectPropertiesMenuCol,
  ProjectPropertiesMenuLayout,
} from '@/features/projects/project-details/components/layouts/project-properties-menu-layout';
import { CategoryItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-category';
import { ClientItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-client';
import { ConnectionItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-connection';
import { ContactItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-contact';
import {
  ContractorsItem,
  InvitedContractor,
} from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-contractors';
import { StatusItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-status';
import { SubcategoryItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-subcategory';
import { TimelineItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-timeline';
import { TrackingIntervalItem } from '@/features/projects/project-details/components/pages/project-details-page/project-properties-menu/project-properties-tracking-interval';
import { ProjectStatus } from '@/features/projects/shared/types/project-status';
import {
  ContractorWithAlias,
  ProjectWithOrgAndParent,
} from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';

type ProjectPropertiesMenuProps = {
  project: ProjectWithOrgAndParent;
  contractors: ContractorWithAlias[];
  category: string;
  subCategory: string;
  startDate?: string | null;
  endDate?: string | null;
  connectedProjectId?: number | null;
  invitedContractors?: InvitedContractor[];
  isNotesExpanded?: boolean;
};

export const ProjectPropertiesMenu = ({
  project,
  contractors,
  invitedContractors = [],
  category,
  subCategory,
  connectedProjectId,
  isNotesExpanded = false,
}: ProjectPropertiesMenuProps) => {
  const isSubproject =
    project.type === 'subproject_contractor' ||
    project.type === 'subproject_client';

  // Calculate columns needed for even distribution in max 2 rows
  // When Notes is enabled, keep 2 columns. When only Properties, arrange evenly (3+3)
  // For 6 standard items, use 3 columns to get 3 items per row
  const gridCols = isNotesExpanded ? 'grid-cols-2' : 'grid-cols-3'; // Always 3 columns when only Properties (3+3 for 6 items)

  return (
    <ProjectPropertiesMenuLayout>
      <div className={cn('grid gap-3', gridCols)}>
        <ClientItem
          clientName={
            project.type === 'subproject_contractor'
              ? project.parent?.org?.name
              : project.org?.name
          }
        />
        <ContractorsItem
          contractors={contractors}
          invitedContractors={invitedContractors}
          projectId={project.id}
          projectType={project.type}
        />
        <CategoryItem category={category} projectType={project.type} />
        {['subproject_client', 'subproject_contractor'].includes(
          project.type,
        ) && <SubcategoryItem subCategory={subCategory} />}
        <ContactItem
          projectId={project.id}
          projectType={project.type}
          projectOrgId={project.org_id}
          mainContact={project.main_contact}
          contractorContact={project.contractor_contact}
          clientContact={project.client_contact}
        />
        <StatusItem
          projectStatus={project.status as ProjectStatus | null}
          projectId={project.id}
        />
        {project.type !== 'subproject_client' && (
          <ConnectionItem
            projectId={project.id}
            connectedProjectId={connectedProjectId}
          />
        )}
        {isSubproject && (
          <TrackingIntervalItem
            project={project}
            trackingInterval={
              project.tracking_interval as TrackingInterval | undefined
            }
          />
        )}
      </div>
      <ProjectPropertiesMenuCol>
        <TimelineItem project={project} />
      </ProjectPropertiesMenuCol>
    </ProjectPropertiesMenuLayout>
  );
};
